import { RotateCcw } from "lucide-react";
import { useDeleteZakazkaCenaOverride } from "@/api/hooks/useZakazkaCenaOverrides";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";

export interface CenaOverrideZnakProps {
  zakazkaId: string;
  materialId: string;
  /** Materiál má v tejto zákazke upravenú cenu (z `useCenaOverrideMap`). */
  upravena: boolean;
  nazov: string;
}

/**
 * Štítok „upravená cena" + tlačidlo na obnovenie cenníkovej ceny.
 *
 * **Prečo to existuje** (KOLO 101): úprava ceny je viazaná na dvojicu
 * `(zákazka, materiál)`, nie na slot ani dielec — takže zmazaním slotu
 * nezanikne a keď materiál pridáš späť, ticho sa použije stará zadaná
 * cena. Presne na to user narazil: *„aj keď ho znova nahráš, pamätá si
 * tú prvú zadanú cenu."*
 *
 * Tlačidlo „Vrátiť na cenník" v `MaterialCenaDialog` existovalo už
 * predtým, ale bolo **schované vnútri dialógu** a na riadku nebolo ani
 * náznaku, že tam nejaká úprava je (tá istá chyba ako checkboxy
 * v KOLE 58 — neviditeľná vec neexistuje).
 *
 * Override sa pri zmazaní slotu vedome **nemaže**: ten istý materiál
 * môže byť priamo na dielci alebo v inom slote a tiché zahodenie
 * zámernej úpravy by bolo horšie než jej prežitie. Riešením je
 * viditeľnosť, nie mazanie.
 */
export function CenaOverrideZnak({
  zakazkaId,
  materialId,
  upravena,
  nazov,
}: CenaOverrideZnakProps): JSX.Element | null {
  const zmazat = useDeleteZakazkaCenaOverride(zakazkaId);
  if (!upravena) return null;

  async function obnov(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    try {
      await zmazat.mutateAsync(materialId);
      toast({
        title: "Cena obnovená z cenníka",
        description: `${nazov} sa v zákazke počíta cenníkovou cenou.`,
      });
    } catch (err) {
      toast({
        title: "Nepodarilo sa obnoviť cenu",
        description: extractErrorMessage(err),
        variant: "destructive",
      });
    }
  }

  return (
    <span className="flex shrink-0 items-center gap-0.5">
      <span
        className="rounded bg-warning/15 px-1 py-0.5 text-[10px] text-warning"
        title="Cena tohto materiálu je pre túto zákazku upravená — nepočíta sa z cenníka"
      >
        upravená cena
      </span>
      <button
        type="button"
        aria-label={`Obnoviť cenu ${nazov} z cenníka`}
        title="Obnoviť cenu z cenníka (zruší úpravu pre túto zákazku)"
        disabled={zmazat.isPending}
        className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary disabled:opacity-50"
        onClick={(e) => void obnov(e)}
      >
        <RotateCcw className="h-3 w-3" />
      </button>
    </span>
  );
}
